import {useState} from 'react';
import {Link, NavLink, useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '../lib/auth';
import {useSite} from '../lib/site-context';
import Icon, {type IconName} from './Icon';

interface NavItem {
  path: string;
  label: string;
  icon: IconName;
  exact?: boolean;
}

const navItems: NavItem[] = [
  {path: '/dashboard', label: '工作台', icon: 'home'},
  {path: '/agent', label: 'AI Agent', icon: 'sparkles'},
  {path: '/sites', label: '网站项目', icon: 'globe'},
  {path: '/catalog', label: '商品素材', icon: 'box'},
  {path: '/design', label: '设计中心', icon: 'palette'},
  {path: '/versions', label: '预览版本', icon: 'eye'},
  {path: '/publish', label: '发布中心', icon: 'rocket'},
];

function isNavActive(pathname: string, item: NavItem) {
  if (item.exact) return pathname === item.path;
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

function SiteSwitcher() {
  const {sites, currentSite, selectSite} = useSite();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  function handleSelect(siteId: string) {
    selectSite(siteId);
    setOpen(false);
    if (location.pathname.startsWith('/agent/sites/')) navigate(`/agent/sites/${siteId}`);
  }

  return <div className="site-switcher-wrap">
    <button type="button" className="site-switcher" aria-label="切换网站" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
      <span className="site-switcher-avatar">{currentSite.initial}</span>
      <span className="site-switcher-copy"><strong>{currentSite.name}</strong><small>{currentSite.domain}</small></span>
      <Icon name="chevronDown" size={15} />
    </button>
    {open && <div className="site-switcher-menu" role="menu" aria-label="选择网站">
      <div className="site-switcher-menu-label">选择当前网站</div>
      {sites.map((site) => <button
        key={site.id}
        type="button"
        role="menuitemradio"
        aria-checked={currentSite.id === site.id}
        aria-label={`选择 ${site.name} ${site.domain}`}
        className={`site-switcher-option ${currentSite.id === site.id ? 'selected' : ''}`}
        onClick={() => handleSelect(site.id)}
      >
        <span className="site-option-avatar">{site.initial}</span>
        <span className="site-option-copy"><strong>{site.name}</strong><small>{site.domain}</small></span>
        {currentSite.id === site.id && <Icon name="check" size={15} />}
      </button>)}
      <Link to="/sites" className="site-switcher-manage" onClick={() => setOpen(false)}>管理网站项目 <Icon name="arrowUpRight" size={13} /></Link>
    </div>}
  </div>;
}

export default function AppLayout({children}: {children: React.ReactNode}) {
  const {user, logout} = useAuth();
  const {currentSite} = useSite();
  const location = useLocation();
  const displayName = user?.name || 'WJ 管理员';
  const displayEmail = user?.email || 'studio@wujiong.io';

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Link to="/agent" className="brand-lockup" aria-label="WJ Studio AI Agent">
          <span className="brand-mark"><Icon name="sparkles" size={17} strokeWidth={2.1} /></span>
          <span><strong>WJ</strong> Studio</span>
        </Link>

        <SiteSwitcher />

        <nav className="sidebar-nav" aria-label="主导航">
          <div className="sidebar-nav-label">工作空间</div>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={() => `sidebar-nav-item ${isNavActive(location.pathname, item) ? 'active' : ''} ${item.path === '/agent' ? 'agent-nav-item' : ''}`}
            >
              <Icon name={item.icon} size={18} />
              <span>{item.label}</span>
              {item.path === '/agent' && <span className="nav-live-dot" />}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <Link to="/settings" className={`sidebar-nav-item sidebar-settings ${isNavActive(location.pathname, {path: '/settings', label: '', icon: 'settings'}) ? 'active' : ''}`}>
            <Icon name="settings" size={18} />
            <span>设置</span>
          </Link>
          <div className="sidebar-divider" />
          <div className="account-row">
            <span className="account-avatar">{displayName.slice(0, 1)}</span>
            <span className="account-copy"><strong>{displayName}</strong><small>{displayEmail}</small></span>
            {user && <button className="icon-button" type="button" onClick={logout} aria-label="退出登录"><Icon name="dots" size={17} /></button>}
          </div>
        </div>
      </aside>

      <main className="app-main">
        <header className="topbar">
          <div className="topbar-breadcrumb"><span className="topbar-site-dot" /> <span>{currentSite.name}</span><span className="topbar-slash">/</span><span className="topbar-current">{getCurrentLabel(location.pathname)}</span></div>
          <div className="topbar-actions">
            <Link to="/versions" className="topbar-link"><Icon name="eye" size={16} />预览</Link>
            <span className="topbar-divider" />
            <span className={`topbar-status ${currentSite.statusTone === 'warning' ? 'pending' : currentSite.statusTone === 'neutral' ? 'offline' : ''}`}><span className={`status-dot ${currentSite.statusTone === 'success' ? 'success' : currentSite.statusTone === 'warning' ? 'warning' : 'neutral'}`} />{currentSite.status}</span>
            <button type="button" className="topbar-help">?</button>
          </div>
        </header>
        <div className="app-content">{children}</div>
      </main>
    </div>
  );
}

function getCurrentLabel(pathname: string) {
  if (pathname.startsWith('/agent')) return 'AI Agent';
  if (pathname.startsWith('/dashboard')) return '工作台';
  if (pathname.startsWith('/sites')) return '网站项目';
  if (pathname.startsWith('/catalog')) return '商品素材';
  if (pathname.startsWith('/design')) return '设计中心';
  if (pathname.startsWith('/versions')) return '预览版本';
  if (pathname.startsWith('/publish')) return '发布中心';
  if (pathname.startsWith('/settings/ai-models')) return 'AI 模型';
  if (pathname.startsWith('/settings')) return '设置';
  return '工作空间';
}
